export const UNSUPPORTED_VALUE = "Other / Unsupported";

export const RISK_LEVELS = ["High", "Medium", "Low", UNSUPPORTED_VALUE];

export const DETAILED_REPORT_CATEGORIES = [
    "Pending for Review",
    "Pending for Discrepancy",
    "Open",
    "In-process",
    "Can Not Comply",
    "Not Complied",
    "Complied On Time",
    "Delayed",
    "Delayed Compliance",
    "Delayed Complied",
    "Not Applicable",
    UNSUPPORTED_VALUE,
];

export const AGEING_BUCKETS = [
    { label: "Not Yet Due", min: Number.NEGATIVE_INFINITY, max: -1 },
    { label: "0-30", min: 0, max: 30 },
    { label: "31-60", min: 31, max: 60 },
    { label: "61-90", min: 61, max: 90 },
    { label: "91-120", min: 91, max: 120 },
    { label: "121-365", min: 121, max: 365 },
    { label: "Over 365", min: 366, max: Number.POSITIVE_INFINITY },
    { label: "Unavailable", invalid: true },
];

const cleanRaw = (value) => String(value ?? "").trim();

export const normalizeValueKey = (value) =>
    cleanRaw(value)
        .toLowerCase()
        .replace(/[_–—]+/g, "-")
        .replace(/\s*-\s*/g, "-")
        .replace(/\s+/g, " ");

const STATUS_ALIASES = new Map([
    ["open", "open"],
    ["pending for review", "pending-for-review"],
    ["pending review", "pending-for-review"],
    ["pending-for-review", "pending-for-review"],
    ["review", "pending-for-review"],
    ["pending for discrepancy", "pending-for-discrepancy"],
    ["pending discrepancy", "pending-for-discrepancy"],
    ["pending-for-discrepancy", "pending-for-discrepancy"],
    ["discrepancy", "pending-for-discrepancy"],
    ["in process", "in-process"],
    ["in-process", "in-process"],
    ["cannot comply", "cannot-comply"],
    ["can not comply", "cannot-comply"],
    ["cannot-comply", "cannot-comply"],
    ["can-not-comply", "cannot-comply"],
    ["not comply", "not-complied"],
    ["not complied", "not-complied"],
    ["not-complied", "not-complied"],
    ["complied", "complied"],
    ["completed", "complied"],
    ["complied on time", "complied-on-time"],
    ["complied-on-time", "complied-on-time"],
    // These are intentionally separate historical/main-status values.
    ["delayed", "delayed"],
    ["delayed compliance", "delayed-compliance"],
    ["delayed-compliance", "delayed-compliance"],
    ["delayed complied", "delayed-complied"],
    ["delayed-complied", "delayed-complied"],
    ["complied late", "delayed-complied"],
    ["not applicable", "not-applicable"],
    ["not-applicable", "not-applicable"],
    ["na", "not-applicable"],
    ["n/a", "not-applicable"],
]);

export const normalizeTrackerStatusKey = (status) => {
    const key = normalizeValueKey(status);
    return STATUS_ALIASES.get(key) || key;
};

export const isKnownTrackerStatus = (status) =>
    STATUS_ALIASES.has(normalizeValueKey(status));

export const canonicalRisk = (risk) => {
    const raw = cleanRaw(risk);
    const key = normalizeValueKey(raw);
    if (["high", "h", "very high", "critical", "severe"].includes(key)) return "High";
    if (["medium", "m", "moderate", "mid", "med"].includes(key)) return "Medium";
    if (["low", "l", "very low", "minor"].includes(key)) return "Low";
    return raw;
};

export const riskBucket = (risk) => {
    const normalized = canonicalRisk(risk);
    return ["High", "Medium", "Low"].includes(normalized)
        ? normalized
        : UNSUPPORTED_VALUE;
};

export const statusMatches = (status, expected) =>
    normalizeTrackerStatusKey(status) === normalizeTrackerStatusKey(expected);

export const statusIn = (status, expectedStatuses) => {
    const key = normalizeTrackerStatusKey(status);
    return expectedStatuses.some((expected) => key === normalizeTrackerStatusKey(expected));
};

export const detailedStatusCategory = (status) => {
    const key = normalizeTrackerStatusKey(status);
    const categories = {
        "pending-for-review": "Pending for Review",
        "pending-for-discrepancy": "Pending for Discrepancy",
        open: "Open",
        "in-process": "In-process",
        "cannot-comply": "Can Not Comply",
        "not-complied": "Not Complied",
        complied: "Complied On Time",
        "complied-on-time": "Complied On Time",
        delayed: "Delayed",
        "delayed-compliance": "Delayed Compliance",
        "delayed-complied": "Delayed Complied",
        "not-applicable": "Not Applicable",
    };
    return categories[key] || UNSUPPORTED_VALUE;
};

const parseDate = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

export const deriveDetailedReportCategory = (tracker) => {
    const key = normalizeTrackerStatusKey(tracker?.status);
    const completed = parseDate(tracker?.completionDate || tracker?.completedAt);
    const due = parseDate(tracker?.dueDate);

    if (["complied", "complied-on-time"].includes(key) && completed && due) {
        return completed.getTime() > due.getTime()
            ? "Delayed Compliance"
            : "Complied On Time";
    }

    return detailedStatusCategory(tracker?.status);
};

export const getAgeingBucket = (daysOverdue) => {
    if (!Number.isFinite(daysOverdue)) return "Unavailable";
    return AGEING_BUCKETS.find(
        (bucket) =>
            !bucket.invalid &&
            daysOverdue >= bucket.min &&
            daysOverdue <= bucket.max
    )?.label || "Unavailable";
};

export const getDaysOverdue = (dueDate, asOfDate) => {
    const due = parseDate(dueDate);
    const asOf = parseDate(asOfDate);
    if (!due || !asOf) return null;
    return Math.ceil((asOf.getTime() - due.getTime()) / 86400000);
};

export const classifyAgeingTracker = (tracker, asOfDate) => {
    const daysOverdue = getDaysOverdue(tracker?.dueDate, asOfDate);
    const bucket = getAgeingBucket(daysOverdue);
    const status = normalizeTrackerStatusKey(tracker?.status);
    const risk = riskBucket(tracker?.risk);
    let category = UNSUPPORTED_VALUE;

    if (status === "open" && risk !== UNSUPPORTED_VALUE) {
        category = `open${risk}`;
    } else if (status === "in-process" && risk !== UNSUPPORTED_VALUE) {
        category = `inProcess${risk}`;
    } else if (status === "cannot-comply") {
        category = "cannotComply";
    } else if (status === "not-complied") {
        category = "notComplied";
    }

    return { bucket, category, daysOverdue, status, risk };
};

export const normalizeSubtaskStatusKey = (status) => {
    const key = normalizeValueKey(status);
    const aliases = {
        pending: "pending",
        "in progress": "in-progress",
        "in-progress": "in-progress",
        complied: "complied",
    };
    return aliases[key] || key;
};

